import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { LockResponse } from "api/ApiClient";
import { BASE_API_URL } from "store/Me";
import { parseLastSeenTimestamp } from "utils/parseLastSeenTimestamp";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { Badge } from "components/ui/badge";
import { statusBadgeOutlineClass } from "shared/utils/statusChipColor";

const SITE_GATEWAYS_QUERY_KEY = "site-gateways";
const DEFAULT_PAGE_SIZE = 50;

async function getSiteGateways(
  siteId: string,
  page: number,
  pageSize: number,
): Promise<LockResponse[]> {
  const urlParams = new URLSearchParams();
  urlParams.append("siteId", siteId);
  urlParams.append("deviceType", "gwe");
  urlParams.append("page", page.toString());
  urlParams.append("pageSize", pageSize.toString());
  const response = await axios.get<LockResponse[]>(
    `${BASE_API_URL}/api/locks?${urlParams.toString()}`,
  );
  return response.data;
}

function useGetSiteGateways(siteId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: [SITE_GATEWAYS_QUERY_KEY, siteId, page, pageSize],
    queryFn: () => getSiteGateways(siteId, page, pageSize),
  });
}

export default function SiteGateways({ siteId }: { siteId: string }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { data, isLoading } = useGetSiteGateways(siteId, page, pageSize);
  const gateways = data ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
        Loading gateways...
      </div>
    );
  }

  return (
    <AdminTable
      aria-label="Site gateways"
      footer={
        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          visibleRowCount={gateways.length}
          isLoading={isLoading}
          onNext={() => setPage((currentPage) => currentPage + 1)}
          onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          disablePrevious={page === 0}
          disableNext={gateways.length < pageSize}
        />
      }
    >
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">System Lock ID</th>
            <th scope="col">Status</th>
            <th scope="col">Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {gateways.map((gw) => (
            <tr key={gw.id}>
              <td>
                <Link to={`/admin/locks/${gw.id}`}>{gw.name}</Link>
              </td>
              <td>{gw.systemLockId ?? "—"}</td>
              <td>
                {gw.onlineStatus && (
                  <Badge
                    className={statusBadgeOutlineClass(
                      gw.onlineStatus === "Online" ? "success" : "default",
                    )}
                    variant="outline"
                  >
                    {gw.onlineStatus}
                  </Badge>
                )}
              </td>
              <td>
                {gw.lastSeenTimeStamp
                  ? parseLastSeenTimestamp(gw.lastSeenTimeStamp)?.toLocaleString() ?? "—"
                  : "—"}
              </td>
            </tr>
          ))}
          {gateways.length === 0 && (
            <tr>
              <td colSpan={4} className="py-16 text-sm text-center text-slate-500">
                No gateways found for this site.
              </td>
            </tr>
          )}
        </tbody>
    </AdminTable>
  );
}
